/**
 * @fileoverview Card component
 * @author Matt Convente (mconvente@yext.com)
 */
goog.declareModuleId('yext.ui.components.card');

import {H3} from '/ui/components/typography/typography';
import {classNames} from '/ui/lib/classnames';
import {useUid} from '/ui/lib/uid';

import * as styles from '/ui/components/card/card.module.scss';

/**
 * @param {{
 *   children?: React.ReactNode,
 *   href?: string,
 *   onClick?: (function():void),
 *   hasNestedInteractiveEl?: boolean,
 *   openLinkInNewWindow?: boolean,
 *   selected?: boolean,
 *   isFullWidth?: boolean,
 *   taskFlowStyle?: boolean,
 *   isDisabled?: boolean,
 *   pendoId?: string,
 * }} props
 * @return {React.ReactElement}
 */
export function Card({
  children,
  href,
  onClick,
  openLinkInNewWindow,
  hasNestedInteractiveEl = false,
  selected = false,
  isFullWidth = false,
  taskFlowStyle = false,
  isDisabled = false,
  pendoId,
}) {
  const contentId = useUid();
  const containerStyles = classNames( styles.card, {
    [styles.nestedInteractiveElStyle]: onClick && hasNestedInteractiveEl,
    [styles.cardFullWidth]: isFullWidth,
    [styles.taskFlowStyle]: taskFlowStyle,
    [styles.disabledStyle]: isDisabled,
  });
  const controlStyles = classNames({
    [styles.actionableCard]: !isDisabled && (href || onClick),
    [styles.selected]: !isDisabled && selected,
  });

  /** @type {?} */
  let cardMarkup = <div className={`${containerStyles} ${controlStyles}`} data-testid="card" data-pendo={pendoId}>{children}</div>;

  if (isDisabled) {
    return cardMarkup;
  }

  if (href && onClick) {
    throw new Error('An actionable Card can only have one of \'href\' or \'onClick\'.');
  }

  if (href) {
    const props = {href};

    if (openLinkInNewWindow) {
      props.target = '_blank';
      props.rel = 'noreferrer';
    }

    cardMarkup = (
      <a
        className={`${containerStyles} ${controlStyles}`}
        data-testid="card"
        data-pendo={pendoId}
        {...props}
      >
        {children}
      </a>
    );
  } else if (onClick) {
    if (hasNestedInteractiveEl) {
      cardMarkup = (
        <div className={styles.buttonCardWrapper}>
          <button
            type="button"
            className={`${containerStyles} ${controlStyles}`}
            onClick={onClick}
            data-testid="card"
            data-pendo={pendoId}
            aria-labelledby={contentId}
          >
          </button>
          <div id={contentId} className={styles.cardChildrenContainer}>
            {children}
          </div>
        </div>
      );
    } else {
      cardMarkup = (
        <button
          type="button"
          className={`${containerStyles} ${controlStyles}`}
          onClick={onClick}
          data-testid="card"
          data-pendo={pendoId}
        >
          {children}
        </button>
      );
    }
  }
  return cardMarkup;
}

/**
 * @param {{
 *   icon?: React.ReactNode,
 *   cardActions?: React.ReactNode,
 *   title?: string,
 *   titleMetadata?: React.ReactNode,
 * }} props
 * @return {?React.ReactElement}
 */
function Header({
  icon,
  cardActions,
  title,
  titleMetadata,
}) {
  /** Generate styles */
  const cardHeaderStyles = classNames(styles.cardHeader, {
    [styles.cardHeaderHasActions]: cardActions,
    [styles.cardHeaderHasIcon]: icon,
  });

  /** Generate markup */

  const cardHeaderIconMarkup = !icon ? null : (
    <div className={styles.cardHeaderIconContainerParent} aria-hidden>
      <div className={styles.cardHeaderIconContainer}>
        {icon}
      </div>
    </div>
  );
  let cardHeaderTitleMarkup;
  if (!title) {
    cardHeaderTitleMarkup = null;
  } else if (!titleMetadata) {
    cardHeaderTitleMarkup = <H3 tid="card-header-title">{title}</H3>;
  } else {
    cardHeaderTitleMarkup = (
      <div className={styles.cardHeaderTitleHasMetadata}>
        <H3 tid="card-header-title">{title}</H3>
        <div data-testid="card-header-title-metadata">
          {titleMetadata}
        </div>
      </div>
    );
  }

  const cardHeaderActionsMarkup = (cardActions) ? (
    <div className={styles.cardHeaderActions} data-testid="card-header-actions">{cardActions}</div>
  ) : null;

  const cardHeaderMarkup = (
    <React.Fragment>
      {cardHeaderIconMarkup}
      {cardHeaderTitleMarkup}
      {cardHeaderActionsMarkup}
    </React.Fragment>
  );

  return <div className={cardHeaderStyles} data-testid="card-header">{cardHeaderMarkup}</div>;
}

/**
 * @param {{
 *   children?: React.ReactNode,
 * }} props
 * @return {React.ReactElement}
 */
function Body({children}) {
  return <div className={styles.cardBody} data-testid="card-body">{children}</div>;
}

Card.Header = Header;
Card.Body = Body;
